import { Employee, Payslip } from '@/types';

/** Payslip after PayslipService mapping, or raw period API row with snake_case joins. */
export type PayslipLike = Payslip & {
  employee_id?: string | number;
  firstName?: string;
  lastName?: string;
  department_name?: string;
  position_title?: string;
  net_salary?: number;
  basic_salary?: number;
  deduction_breakdown?: Array<{ name?: string; amount?: number }>;
  allowance_breakdown?: Array<{ name?: string; amount?: number }>;
  swiftCode?: string;
};

const asPayslipLike = (payslip: Payslip): PayslipLike => payslip as PayslipLike;

/** Match payslip to roster by internal id, business employee number, or both. */
export function findEmployeeForPayslip(
  payslip: Payslip,
  employees: Employee[]
): Employee | undefined {
  const p = asPayslipLike(payslip);
  const internalId = String(p.employee_id ?? p.employeeId ?? '');
  const businessId = String(p.employeeIdNumber ?? '');
  const payslipKey = String(p.employeeId ?? '');

  return employees.find((emp) => {
    const empId = String(emp.id);
    const empNo = String(emp.employeeId ?? '');
    return (
      (payslipKey && empId === payslipKey) ||
      (internalId && empId === internalId) ||
      (businessId && empNo === businessId) ||
      (payslipKey && empNo === payslipKey)
    );
  });
}

function businessEmployeeId(
  payslip: PayslipLike,
  employee?: Employee
): string {
  if (employee?.employeeId) return employee.employeeId;
  if (payslip.employeeIdNumber) return String(payslip.employeeIdNumber);
  if (
    payslip.employee_id != null &&
    String(payslip.employeeId) !== String(payslip.employee_id)
  ) {
    return String(payslip.employeeId);
  }
  return String(payslip.employeeId ?? payslip.employee_id ?? '');
}

/** Employee display + bank fields for reports (roster, then payslip join, then raw API). */
export function resolvePayslipEmployee(
  payslip: Payslip,
  employees: Employee[]
) {
  const p = asPayslipLike(payslip);
  const employee = findEmployeeForPayslip(payslip, employees);
  const firstName = employee?.firstName ?? String(p.firstName ?? '');
  const lastName = employee?.lastName ?? String(p.lastName ?? '');
  const name =
    (payslip.employeeName || `${firstName} ${lastName}`.trim()) || 'Unknown';

  return {
    employee,
    employeeId: businessEmployeeId(p, employee),
    name,
    division:
      employee?.division ??
      payslip.division ??
      String(p.department_name ?? 'N/A'),
    jobTitle:
      employee?.jobTitle ??
      payslip.jobTitle ??
      String(p.position_title ?? 'N/A'),
    nrc: employee?.nrc ?? payslip.nrc ?? 'N/A',
    manNumber:
      employee?.manNumber ??
      payslip.manNumber ??
      String((p as { man_number?: string }).man_number ?? 'N/A'),
    taxId: employee?.taxId ?? payslip.taxId ?? 'N/A',
    bankName: employee?.bankName ?? payslip.bankName ?? 'N/A',
    bankAccount: employee?.bankAccount ?? payslip.bankAccount ?? 'N/A',
    bankBranch: employee?.bankBranch ?? payslip.bankBranch ?? 'N/A',
    swiftcode: employee?.swiftcode ?? payslip.swiftcode ?? p.swiftCode ?? '',
  };
}

export function matchPayslipToPrevious(
  current: Payslip,
  previous: Payslip,
  employees: Employee[]
): boolean {
  const cur = asPayslipLike(current);
  const prev = asPayslipLike(previous);
  const a = resolvePayslipEmployee(current, employees);
  const b = resolvePayslipEmployee(previous, employees);
  if (a.employeeId && b.employeeId && a.employeeId === b.employeeId) {
    return true;
  }
  const internalA = String(cur.employee_id ?? current.employeeId ?? '');
  const internalB = String(prev.employee_id ?? previous.employeeId ?? '');
  return Boolean(internalA && internalB && internalA === internalB);
}

export function payslipNetSalary(payslip: Payslip): number {
  const p = asPayslipLike(payslip);
  return Number(payslip.netSalary ?? p.net_salary ?? 0);
}

export function payslipBasicSalary(payslip: Payslip): number {
  const p = asPayslipLike(payslip);
  return Number(payslip.basicSalary ?? p.basic_salary ?? payslip.basicPay ?? 0);
}

export function payslipDeductionBreakdown(payslip: Payslip): Array<{
  name?: string;
  amount?: number;
  employer_amount?: number;
}> {
  const p = asPayslipLike(payslip);
  return payslip.deductionBreakdown ?? p.deduction_breakdown ?? [];
}

export function payslipAllowanceBreakdown(
  payslip: Payslip
): Array<{ name?: string; amount?: number; type?: string }> {
  const p = asPayslipLike(payslip);
  return payslip.allowanceBreakdown ?? p.allowance_breakdown ?? [];
}

export type StatutoryDeductionType = 'paye' | 'napsa' | 'nhima';

function matchesStatutory(name: string, type: StatutoryDeductionType): boolean {
  const lower = name.toLowerCase();
  if (type === 'paye') {
    return lower.includes('paye') || lower.includes('income tax');
  }
  if (type === 'napsa') {
    return lower.includes('napsa') || lower.includes('pension') || lower.includes('nssf');
  }
  return (
    lower.includes('nhima') ||
    lower.includes('lasf') ||
    lower.includes('nhis') ||
    lower.includes('health insurance')
  );
}

/** Statutory amounts from payslip deduction lines (not legacy top-level fields). */
export function payslipStatutoryAmount(
  payslip: Payslip,
  type: StatutoryDeductionType
): number {
  return payslipDeductionBreakdown(payslip)
    .filter((d) => d.name && matchesStatutory(d.name, type))
    .reduce((s, d) => s + Number(d.amount || 0), 0);
}

function payslipGrossPay(payslip: Payslip): number {
  const gross = Number(payslip.grossSalary);
  if (gross > 0) return gross;
  return (
    Number(payslip.basicSalary ?? payslip.basicPay ?? 0) +
    Number(payslip.totalAllowances ?? 0)
  );
}

const EMPLOYER_NAPSA_RATE = 0.05;
const EMPLOYER_NHIMA_RATE = 0.01;

function payrollIncludesStatutoryDeductions(payslip: Payslip): boolean {
  return payslipDeductionBreakdown(payslip).some(
    (d) =>
      d.name &&
      (matchesStatutory(d.name, 'paye') ||
        matchesStatutory(d.name, 'napsa') ||
        matchesStatutory(d.name, 'nhima'))
  );
}

function estimateEmployerNapsa(payslip: Payslip): number {
  const basic = Number(payslip.basicSalary ?? payslip.basicPay ?? 0);
  const base = basic > 0 ? basic : payslipGrossPay(payslip);
  return Math.round(base * EMPLOYER_NAPSA_RATE * 100) / 100;
}

function estimateEmployerNhima(payslip: Payslip): number {
  return Math.round(payslipGrossPay(payslip) * EMPLOYER_NHIMA_RATE * 100) / 100;
}

/** Employer share for a deduction line (reports/remittance only).
 * NAPSA/NHIMA: mirror employee amount when no snapshot.
 * Others: use employer_amount from payroll (catalog employer %).
 */
export function payslipLineEmployerAmount(line: {
  name?: string;
  amount?: number;
  employer_amount?: number;
}): number {
  const snap = Number(line.employer_amount || 0);
  if (snap > 0) return snap;
  const name = String(line.name || '');
  const employeeAmt = Number(line.amount || 0);
  if (matchesStatutory(name, 'napsa') || matchesStatutory(name, 'nhima')) {
    return employeeAmt;
  }
  return 0;
}

/** Employer NAPSA is 5% — same amount as the employee's 5% NAPSA deduction. */
export function payslipEmployerNapsa(payslip: Payslip): number {
  const fromBreakdown = payslipStatutoryAmount(payslip, 'napsa');
  if (fromBreakdown > 0) return fromBreakdown;
  if (payrollIncludesStatutoryDeductions(payslip)) {
    return estimateEmployerNapsa(payslip);
  }
  return 0;
}

/** Employer NHIMA is 1% — same amount as the employee's 1% NHIMA deduction. */
export function payslipEmployerNhima(payslip: Payslip): number {
  const fromBreakdown = payslipStatutoryAmount(payslip, 'nhima');
  if (fromBreakdown > 0) return fromBreakdown;
  if (payrollIncludesStatutoryDeductions(payslip)) {
    return estimateEmployerNhima(payslip);
  }
  return 0;
}

/** Wage bill = gross + employer NAPSA (5%) + employer NHIMA (1%). */
export function payslipWageBill(payslip: Payslip): number {
  return (
    payslipGrossPay(payslip) +
    payslipEmployerNapsa(payslip) +
    payslipEmployerNhima(payslip)
  );
}
